// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — PDF Export Service
// Captures rendered A4 pages from the viewer DOM
// ═══════════════════════════════════════════════════════════════

import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

// ─── A4 mm ───
const A4_W = 210;
const A4_H = 297;

// ─── Watermark ───
function watermark(pdf: jsPDF) {
  const n = pdf.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    pdf.setPage(i);
    pdf.setFontSize(36);
    pdf.setTextColor(200, 200, 200);
    const cx = A4_W / 2;
    const cy = A4_H / 2;
    pdf.text('COPIA CONTROLADA', cx, cy - 8, { angle: 45, align: 'center' });
    pdf.setFontSize(20);
    pdf.text('PROHIBIDA SU DISTRIBUCIÓN', cx, cy + 10, { angle: 45, align: 'center' });
  }
}

// ═══ Capture viewer pages to PDF ═══
// Finds all [data-a4-page] elements inside the given container,
// captures each one with html2canvas, and adds it as a full-bleed
// A4 page in the PDF. The result is pixel-identical to the viewer.
export async function exportViewerToPdf(container: HTMLElement, filename: string): Promise<void> {
  const pages = container.querySelectorAll<HTMLElement>('[data-a4-page]');
  if (pages.length === 0) return;

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];

    // Temporarily remove transform scaling so html2canvas captures at native 794×1123
    const savedTransform = page.style.transform;
    const savedOrigin = page.style.transformOrigin;
    page.style.transform = 'none';
    page.style.transformOrigin = '';

    const canvas = await html2canvas(page, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      width: page.offsetWidth,
      height: page.offsetHeight,
    });

    // Restore transform
    page.style.transform = savedTransform;
    page.style.transformOrigin = savedOrigin;

    const imgData = canvas.toDataURL('image/png');

    if (i > 0) pdf.addPage();
    // Full-bleed: image fills the entire A4 page (0,0 to 210×297)
    pdf.addImage(imgData, 'PNG', 0, 0, A4_W, A4_H);
  }

  watermark(pdf);
  pdf.save(filename);
}

// ═══ Legacy: export document from structured data ═══
// Kept for DocumentPreviewPanel which may not have the viewer DOM
export async function exportDocumentToPdf(opts: {
  codigo: string;
  nombre: string;
  empresa?: string;
  edicion?: string;
  fecha?: string;
  proceso?: string;
  tipoDocumental?: string;
  contenidoHtml: string;
}): Promise<void> {
  // Try to find the viewer DOM first (preferred path)
  const viewerContainer = document.querySelector<HTMLElement>('.viewer-print-area');
  if (viewerContainer) {
    const filename = `${opts.codigo} ${opts.nombre}.pdf`;
    return exportViewerToPdf(viewerContainer, filename);
  }

  // Fallback: should not normally reach here
  console.warn('[exportPdf] No viewer DOM found, skipping export');
}

// ═══ Export ficha from DOM element ═══
export async function exportFichaToPdf(
  element: HTMLElement,
  opts: { codigo: string; nombre: string; proceso: string },
): Promise<void> {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#fff',
  });

  const imgW = A4_W;
  const ratio = canvas.width / canvas.height;
  const totalH = imgW / ratio;
  const pages = Math.max(1, Math.ceil(totalH / A4_H));

  for (let p = 0; p < pages; p++) {
    if (p > 0) pdf.addPage();
    const srcY = Math.round(((p * A4_H) / totalH) * canvas.height);
    const srcH = Math.round((A4_H / totalH) * canvas.height);
    const sliceH = Math.min(srcH, canvas.height - srcY);
    if (sliceH <= 0) break;

    const sc = document.createElement('canvas');
    sc.width = canvas.width;
    sc.height = sliceH;
    const ctx = sc.getContext('2d')!;
    ctx.drawImage(canvas, 0, srcY, canvas.width, sliceH, 0, 0, sc.width, sc.height);

    const data = sc.toDataURL('image/png');
    const h = (sliceH / canvas.width) * imgW;
    pdf.addImage(data, 'PNG', 0, 0, imgW, h);
  }

  watermark(pdf);
  pdf.save(`${opts.codigo} ${opts.nombre}.pdf`);
}
